import type { LandingPageContent } from "../types";
import { dataRoomHandoffContent } from "../dataRoomHandoff";
import { payrollContent } from "../payroll";

export const enContent: LandingPageContent = {
  locale: "en",
  htmlLang: "en",
  routePath: "/en",
  meta: {
    title: "DynamicBooks - agent-driven accounting, VAT and reports",
    description:
      "Request a non-binding service estimate for bookkeeping, VAT support, bank matching, SIE migration, reports, Data Rooms, and expected Agent Credit capacity with human review."
  },
  brand: {
    name: "DynamicBooks",
    homeAriaLabel: "DynamicBooks home"
  },
  navigation: {
    skipToContentLabel: "Skip to content",
    localeSwitchLabel: "Svenska",
    localeSwitchAriaLabel: "Switch to Swedish",
    mobileMenuOpenLabel: "Open navigation",
    mobileMenuCloseLabel: "Close navigation",
    mobileMenuPanelLabel: "Navigation menu",
    items: [
      { kind: "link", id: "services", label: "Services", href: "#services" },
      { kind: "link", id: "accounting-agent", label: "Accounting Agent", href: "#accounting-agent" },
      { kind: "link", id: "data-rooms", label: "Data Rooms", href: "#data-rooms" },
      { kind: "link", id: "quickbooks", label: "QuickBooks", href: "/quickbooks" },
      {
        kind: "menu",
        id: "software",
        label: "Software",
        href: "#software-pricing",
        items: [
          { id: "reports", label: "Reports", href: "#reports" },
          { id: "payroll", label: "Payroll", href: "#payroll" },
          { id: "bas", label: "BAS", href: "#reports" },
          { id: "ap-ar", label: "AP/AR", href: "#reports" },
          { id: "sie", label: "SIE", href: "#reports" },
          { id: "fixed-assets", label: "Fixed assets", href: "#reports" }
        ]
      },
      { kind: "link", id: "software-pricing", label: "Pricing", href: "#software-pricing" },
      { kind: "cta", id: "estimate", label: "Request Estimate", href: "#estimate" },
      { kind: "secondary", id: "login", label: "Log in", href: "#login-handoff" }
    ]
  },
  hero: {
    eyebrow: "ACCOUNTING, REPORTING, AND AGENT SUPPORT",
    title: "Accounting services for companies that need cleaner bookkeeping, VAT support, and less manual admin",
    lead:
      "DynamicBooks combines accounting software, secure Data Rooms, agent-assisted preparation, and human review so your transactions, reports, VAT material, and accounting evidence stay organized and traceable.",
    primaryCta: { label: "Request a service estimate", href: "#estimate" },
    secondaryCta: { label: "View software pricing", href: "#software-pricing" },
    trustCues: [
      "Bookkeeping support",
      "VAT reporting preparation",
      "Bank matching",
      "SIE migration",
      "AP/AR review",
      "Data Rooms",
      "Reports & evidence"
    ]
  },
  audience: {
    id: "audience",
    eyebrow: "Audience",
    title: "Who is DynamicBooks for?",
    lead: "Built for businesses that want less accounting administration and more control",
    groups: [
      {
        id: "designed-for",
        title: "DynamicBooks is designed for",
        items: [
          "Swedish small and medium-sized businesses",
          "International founders operating Swedish entities",
          "Growing companies with increasing accounting workloads",
          "Businesses managing recurring bookkeeping, VAT reporting, and reporting requirements",
          "Companies planning migrations, cleanup projects, or accounting catch-up work"
        ]
      },
      {
        id: "helps",
        title: "How DynamicBooks helps",
        items: [
          "Reduce manual bookkeeping and finance administration",
          "Keep transactions, documents, and reports organized",
          "Prepare VAT reporting and financial reports with greater confidence",
          "Simplify migrations, reconciliations, and ongoing accounting operations",
          "Maintain clear evidence and traceability across accounting workflows"
        ]
      },
      {
        id: "approach",
        title: "The DynamicBooks approach",
        items: [
          "By combining accounting software, AI agents, secure Data Rooms, and human review, DynamicBooks provides a structured path from onboarding through recurring finance operations."
        ]
      }
    ]
  },
  seoInfoCards: {
    id: "seo-info",
    eyebrow: "Key topics",
    title: "Everything you need to manage your accounting.",
    lead:
      "DynamicBooks brings together the tools and services businesses need to manage bookkeeping, VAT, banking, reporting, migrations, and year-end accounting in one structured workflow. Explore the key areas below to see how each part supports your accounting process.",
    groups: [
      {
        id: "bookkeeping",
        title: "Bookkeeping",
        itemIds: ["bas-chart", "bank-flows", "ap-ar"]
      },
      {
        id: "compliance-reporting",
        title: "Compliance & Reporting",
        itemIds: ["vat-report", "year-end", "sie-migration"]
      },
      {
        id: "collaboration-platform",
        title: "Collaboration & Platform",
        itemIds: ["data-rooms", "agent-credits"]
      }
    ],
    items: [
      {
        id: "bas-chart",
        title: "BAS Chart of Accounts",
        body: "Manage your BAS chart of accounts with clear account structures, balances, VAT mappings, and full traceability for Swedish accounting.",
        keyword: "Swedish accounting software",
        frontAssetId: "seo-bas-chart-front",
        assetId: "seo-bas-chart"
      },
      {
        id: "vat-report",
        title: "VAT Reports",
        body: "Prepare VAT reports with built-in validation, discrepancy checks, and supporting documentation before submission.",
        keyword: "VAT report Sweden",
        frontAssetId: "seo-vat-report-front",
        assetId: "seo-vat-report"
      },
      {
        id: "bank-flows",
        title: "Bank Matching",
        body: "Import bank transactions, reconcile payments, and identify unmatched or missing records more efficiently.",
        keyword: "bank integration accounting",
        frontAssetId: "seo-bank-flows-front",
        assetId: "seo-bank-flows"
      },
      {
        id: "sie-migration",
        title: "SIE Migration",
        body: "Import existing accounting data using the Swedish SIE format while preserving historical records and accounting context.",
        keyword: "SIE Migration",
        frontAssetId: "seo-sie-migration-front",
        assetId: "seo-sie-migration"
      },
      {
        id: "ap-ar",
        title: "AP/AR",
        body: "Track customer invoices, supplier bills, payment status, and outstanding balances from one place.",
        keyword: "accounts payable receivable",
        frontAssetId: "seo-ap-ar-front",
        assetId: "seo-ap-ar"
      },
      {
        id: "year-end",
        title: "Year-end Support",
        body: "Prepare year-end reports, closing activities, and supporting documentation with structured review workflows.",
        keyword: "year-end accounting support",
        frontAssetId: "seo-year-end-front",
        assetId: "seo-year-end"
      },
      {
        id: "data-rooms",
        title: "Data Rooms",
        body: "Store accounting records, supporting documents, reports, and communication in secure company workspaces with full traceability.",
        keyword: "accounting data room",
        frontAssetId: "seo-data-rooms-front",
        assetId: "seo-data-rooms"
      },
      {
        id: "agent-credits",
        title: "Accounting Agent",
        body: "Agent credits represent the processing capacity used by AI accounting agents to prepare bookkeeping, VAT reports, reconciliations, migrations, and other accounting workflows. Your estimate includes the expected capacity and monthly Agent Credit usage based on your company's accounting activity.",
        keyword: "Agent Credits",
        frontAssetId: "seo-agent-credits-front",
        assetId: "seo-agent-credits"
      }
    ]
  },
  quotePanel: {
    eyebrow: "Service estimate",
    title: "Get a preliminary service estimate.",
    summary:
      "Describe your company, monthly volume, VAT period, bank sources, reporting needs, and any migration or cleanup work. DynamicBooks gives a non-binding service view before final scope is confirmed.",
    expandLabel: "Start estimate request",
    collapseLabel: "Close estimate request",
    previewTitle: "",
    resultBackLabel: "Back to estimate form",
    fields: [
      { label: "Company details", value: "Name, email, registration or VAT id" },
      { label: "Monthly accounting volume", value: "Transactions, invoices, receipts, payroll" },
      { label: "Scope and migration needs", value: "SIE, cleanup, year-end, fixed assets" }
    ],
    lanes: ["Request", "Bid generation", "Onboarding"],
    disclaimer: "All estimates are preliminary and non-binding until evidence, sources, scope, and responsibility level are confirmed."
  },
  services: {
    id: "services",
    eyebrow: "Services",
    title: "Accounting services built around your workload.",
    lead:
      "Every company has different accounting requirements. Instead of offering a fixed-price list, DynamicBooks estimates the appropriate service based on transaction volume, accounting complexity, reporting requirements, data sources, and the level of agent and human support required.",
    leadSecondary:
      "Every accounting service combines AI accounting agents, structured workflows, and human review. Agent-assisted preparation helps reduce manual work while keeping accounting decisions transparent, traceable, and under control.",
    items: [
      {
        title: "Monthly bookkeeping",
        body: "Recurring bookkeeping that keeps bank transactions, receipts, supplier invoices, customer invoices, and VAT periods organized through structured agent-assisted workflows with human review where needed.",
        tags: ["Transactions", "Receipts", "VAT periods"],
        assetId: "delivery-software-first"
      },
      {
        title: "Bank matching",
        body: "Import bank transactions from supported sources, automatically prepare matching suggestions, identify exceptions, and review postings before they become part of your accounting records.",
        tags: ["Bank imports", "Payment matching", "Exception review"],
        assetId: "delivery-hybrid"
      },
      {
        title: "VAT support",
        body: "Prepare VAT reports with automated validation, discrepancy checks, supporting evidence, and report artifacts before filing or review.",
        tags: ["VAT validation", "Report artifacts", "Filing preparation"],
        assetId: "delivery-review-evidence"
      },
      {
        title: "Migration",
        body: "Migrate historical accounting data, clean up previous bookkeeping, manage fixed assets, prepare annual reports, and complete year-end activities through structured migration and review workflows.",
        tags: ["SIE migration", "Historical cleanup", "Year-end support"],
        assetId: "delivery-human-assisted"
      }
    ]
  },
  accountingAgent: {
    id: "accounting-agent",
    eyebrow: "Accounting Agent",
    title: "AI accounting agents prepare the work. People stay in control.",
    lead:
      "DynamicBooks uses AI accounting agents to prepare bookkeeping, VAT reporting, reconciliations, migrations, and report material inside controlled workflows. Human review, scope control, and confirmation remain part of the delivery before accounting work moves forward.",
    illustration: {
      ariaLabel:
        "DynamicBooks workflow showing company accounting material entering a secure Data Room, where AI accounting agents and human reviewers work together before producing bookkeeping, VAT reports, financial reports, and traceable evidence.",
      input: {
        kicker: "COMPANY",
        title: "Accounting material",
        body: "Transactions, documents, bank data, VAT periods, and reporting needs"
      },
      dataRoom: {
        kicker: "SECURE WORKSPACE",
        title: "DynamicBooks Data Room",
        status: "Traceable",
        items: ["Accounting records", "Bank sources", "VAT material", "Reports", "Supporting evidence"]
      },
      collaboration: {
        agent: {
          kicker: "PREPARATION",
          title: "AI accounting agents",
          responsibilities: [
            "Read structured accounting material",
            "Prepare bookkeeping suggestions",
            "Check VAT and reporting inputs",
            "Identify exceptions and missing evidence",
            "Create dry-runs and report drafts"
          ],
          credits: {
            label: "Agent Credits",
            title: "Measured processing capacity",
            body: "Estimates include the expected capacity required for the company's accounting workflows."
          }
        },
        linkLabel: "Controlled workflow",
        human: {
          kicker: "CONTROL",
          title: "Human review and scope control",
          responsibilities: [
            "Review exceptions and discrepancies",
            "Validate responsibility-sensitive decisions",
            "Confirm scope and delivery assumptions",
            "Handle complex or higher-risk cases",
            "Approve the next accounting step"
          ],
          review: {
            status: "Ready for confirmation",
            body: "SIE migration, cleanup, payroll, year-end work, and other complex cases follow additional checks where required."
          }
        }
      },
      confirmation: {
        kicker: "CONFIRMED WORKFLOW",
        title: "Scope, responsibility, assumptions, and next steps confirmed"
      },
      outputs: [
        {
          title: "Bookkeeping",
          body: "Prepared transactions, matching suggestions, and review history"
        },
        {
          title: "VAT",
          body: "Validated VAT material, discrepancy checks, and report artifacts"
        },
        {
          title: "Reports",
          body: "Financial reports, report packages, and supporting material"
        },
        {
          title: "Evidence",
          body: "Traceable decisions, previews, files, receipts, and confirmations"
        }
      ],
      processLabel: "Accounting Agent process"
    },
    steps: [
      {
        title: "Prepare",
        body: "Collect company information, accounting volume, bank sources, VAT periods, reporting requirements, and migration details.",
        assetId: "agent-prepare"
      },
      {
        title: "Dry-run",
        body: "Generate suggestions, identify exceptions, and prepare VAT and reporting material without changing accounting records.",
        assetId: "agent-dry-run"
      },
      {
        title: "Check",
        body: "Route migrations, cleanup, payroll, year-end work, and complex cases through the appropriate scope and review controls.",
        assetId: "agent-review"
      },
      {
        title: "Confirm",
        body: "Confirm scope, responsibilities, delivery approach, Agent Credit capacity, and next steps before accounting work begins.",
        assetId: "agent-confirm"
      }
    ]
  },
  dataRooms: {
    id: "data-rooms",
    eyebrow: dataRoomHandoffContent.eyebrow.en,
    title: dataRoomHandoffContent.title.en,
    lead: dataRoomHandoffContent.lead.en,
    boundary: dataRoomHandoffContent.boundary.en,
    steps: dataRoomHandoffContent.stages.map((stage) => ({
      title: stage.title.en,
      body: stage.body.en,
      assetId: stage.assetId
    }))
  },
  reports: {
    id: "reports",
    eyebrow: "Reports",
    title: "Reports and traceable accounting evidence",
    lead:
      "DynamicBooks helps companies see what the numbers rely on, which questions remain, and which decisions have been made.",
    modules: [
      {
        key: "bas_account_plan",
        title: "BAS chart of accounts",
        body: "Account categories, SIE account types, VAT-code mapping, validity, and active/inactive controls.",
        detailLabel: "Chart",
        assetId: "module-chart-balances"
      },
      {
        key: "account_balances",
        title: "Account balances",
        body: "Balance snapshots by selected date for account number, name, category, and balance.",
        detailLabel: "Balances",
        assetId: "module-chart-balances"
      },
      {
        key: "general_ledger",
        title: "General ledger",
        body: "Account and date drilldown with debit, credit, verification labels, and descriptions.",
        detailLabel: "Ledger",
        assetId: "module-ledger-journals"
      },
      {
        key: "journal_list",
        title: "Journal list",
        body: "Searchable journal entries and verification list context with relation links, posting state, and exports.",
        detailLabel: "Journals",
        assetId: "module-ledger-journals"
      },
      {
        key: "balance_sheet",
        title: "Balance sheet",
        body: "Fiscal-year context, account rows, opening/closing balances, deltas, and HTML/PDF output.",
        detailLabel: "Balance",
        assetId: "module-vat-reports"
      },
      {
        key: "profit_loss",
        title: "Profit and loss",
        body: "Period and fiscal-year views with income, expense, result totals, account rows, and export.",
        detailLabel: "Result",
        assetId: "module-vat-reports"
      },
      {
        key: "vat_reports_eskd_pdf_xml",
        title: "VAT reports, PDF, XML/eSKD",
        body: "Prepare and validate VAT reports, generate PDF and XML/eSKD artifacts, and record filing evidence without filing guarantees.",
        detailLabel: "VAT",
        assetId: "module-vat-reports"
      },
      {
        key: "bank_transactions_revolut_sources",
        title: "Bank transactions and Revolut sources",
        body: "Check, import, sync where configured, suggest, and book bank transactions; Revolut source management is evidenced locally.",
        detailLabel: "Bank",
        assetId: "module-bank-records"
      },
      {
        key: "ap_ar_ledgers",
        title: "AP/AR ledgers",
        body: "Open/history ledgers, invoice verification linking, payment status, candidate search, and mark-paid workflow.",
        detailLabel: "Ledgers",
        assetId: "module-ap-ar"
      },
      {
        key: "sie_import_export_dedupe",
        title: "SIE import/export/dedupe",
        body: "Import/export with checks, series mapping, duplicate checks, correction chains, and payment matching.",
        detailLabel: "SIE",
        assetId: "module-sie-migration"
      },
      {
        key: "fixed_assets_depreciation",
        title: "Fixed assets and depreciation",
        body: "Asset register, depreciation/write-down support, journal-line roles, reconciliation, and exports.",
        detailLabel: "Assets",
        assetId: "module-fixed-assets"
      },
      {
        key: "reports_register_annual_artifacts",
        title: "Reports register and annual artifacts",
        body: "Catalog, previews, generated artifacts, run history, filing-status records, and annual/SKV-style artifacts where configured.",
        detailLabel: "Reports",
        assetId: "module-report-register"
      }
    ]
  },
  payroll: payrollContent.en,
  quickBooks: {
    id: "quickbooks",
    eyebrow: "QuickBooks Online",
    title: "QuickBooks Online connection",
    lead: "Review Accounting-only permissions and connection availability. Payments is not included in the current connection flow.",
    action: { href: "/quickbooks", label: "Review QuickBooks connection" }
  },
  estimate: {
    id: "estimate",
    eyebrow: "Estimate path",
    title: "Preliminary service estimate without a public fixed-price table",
    lead:
      "The estimate combines platform access, accounting work execution, expected Agent Credits, and scope signals without treating public copy as final billing.",
    packages: [
      {
        title: "Start",
        body: "Lower-volume recurring bookkeeping with focused VAT/reporting needs.",
        assetId: "pricing-start"
      },
      {
        title: "Growth",
        body: "More transactions, multiple sources, AP/AR, reporting cadence, or moderate scope needs.",
        assetId: "pricing-growth"
      },
      {
        title: "Operator",
        body: "Operational accounting workstreams, data rooms, advanced reports, and higher onboarding intensity.",
        assetId: "pricing-operator"
      },
      {
        title: "Migration/Cleanup",
        body: "SIE migration, catch-up, cleanup, fixed assets, or year-end/report package work.",
        assetId: "pricing-cleanup"
      }
    ],
    notes: [
      "Final price, scope, responsibility level, and start date are confirmed after source and scope checks.",
      "Agent Credits describe expected run capacity, not final billing.",
      "Complex or high-scope cases move through guided onboarding."
    ]
  },
  faq: {
    id: "faq",
    eyebrow: "FAQ",
    title: "Common questions before the estimate",
    items: [
      {
        question: "What is DynamicBooks?",
        answer:
          "DynamicBooks is an accounting service and agent-driven workflow for companies that need structure around transactions, VAT, reports, and accounting evidence."
      },
      {
        question: "Is the estimate binding?",
        answer: "No. The estimate is preliminary until evidence, sources, responsibility level, and scope are confirmed."
      },
      {
        question: "Can DynamicBooks handle VAT reports?",
        answer:
          "DynamicBooks can prepare and validate VAT reports, VAT evidence, and artifacts such as PDF and XML/eSKD where the workflow is configured."
      },
      {
        question: "Which bank sources can be used?",
        answer:
          "Bank files, manual imports, and Revolut sources can be included where configured. DynamicBooks does not promise universal bank coverage."
      },
      {
        question: "Can I import SIE?",
        answer:
          "SIE import, export, and duplicate checks can be part of migration or historical cleanup and are handled during onboarding scope checks."
      },
      {
        question: "How do billing and subscriptions work?",
        answer:
          "DynamicBooks only shows non-binding recommendations. Billing, payment, and account or subscription management happen in DynamicMail after login."
      },
      {
        question: "What are Agent Credits?",
        answer:
          "Agent Credits describe expected run capacity for agent work in Data Rooms. They are a planning signal, not a final billing decision."
      },
      {
        question: "When is guided onboarding needed?",
        answer:
          "Guided onboarding is used for SIE, cleanup, year-end work, payroll volume, unknown sources, risk markers, or low confidence in the estimate."
      },
      {
        question: "What payroll processing does DynamicBooks support?",
        answer:
          "DynamicBooks supports payroll imports, calculations, batch checks, reimbursements, journal previews, payout draft export, tax table reference, and report artifacts. Filing responsibility and payroll service scope are confirmed separately."
      },
      {
        question: "How are Data Rooms used?",
        answer:
          "Data Rooms collect evidence, dry-runs, scope control signals, report artifacts, and the handoff into DynamicMail for continued delivery."
      }
    ]
  },
  footer: {
    id: "footer",
    owner: "DynamicBooks by Eregis",
    summary:
      "Accounting software, reports, Data Rooms, and agent support for companies in Sweden.",
    copyright: "© 2026 Eregis, Inc.",
    groups: [
      {
        id: "product",
        title: "Product",
        links: [
          { id: "software", label: "Software", href: "#software-pricing" },
          { id: "services", label: "Services", href: "#services" },
          { id: "pricing", label: "Pricing", href: "#software-pricing" },
          { id: "reports", label: "Reports", href: "#reports" }
        ]
      },
      {
        id: "workflows",
        title: "Workflows",
        links: [
          { id: "agentdriven-article", label: "Agent-driven accounting article", href: "https://dynamicbooks.se/artiklar/agentdriven-bokforing" },
          { id: "accounting-agent", label: "Accounting Agent", href: "#accounting-agent" },
          { id: "data-rooms", label: "Data Rooms", href: "#data-rooms" },
          { id: "bas-vat", label: "BAS and VAT", href: "#seo-info" },
          { id: "sie-migration", label: "SIE and migration", href: "#reports" }
        ]
      },
      {
        id: "trust",
        title: "Trust",
        links: [
          { id: "privacy", label: "Privacy", href: "https://eregis.com/privacy" },
          { id: "terms", label: "Terms", href: "https://eregis.com/terms" },
          { id: "quickbooks-terms", label: "QuickBooks terms", href: "/quickbooks/terms" },
          { id: "quickbooks-privacy", label: "QuickBooks privacy notice", href: "/quickbooks/privacy" },
          { id: "cookies", label: "Cookie settings", href: "#footer", action: "cookie-settings" },
          { id: "contact", label: "Contact", href: "#estimate" }
        ]
      },
      {
        id: "access",
        title: "Access",
        links: [
          { id: "login", label: "Log in", href: "#login-handoff" },
          { id: "estimate", label: "Request a service estimate", href: "#estimate" }
        ]
      }
    ],
    cmp: {
      bannerTitle: "Cookies and privacy",
      bannerBody:
        "We use necessary cookies to make the website work. If analytics are used, you should be able to choose that separately.",
      necessaryOnlyLabel: "Necessary only",
      acceptAllLabel: "Accept all",
      settingsLabel: "Settings",
      settingsTitle: "Cookie settings",
      necessaryCategory: "Necessary: required for basic function and security.",
      analyticsCategory: "Analytics: helps us understand how the website is used if you consent.",
      analyticsToggleLabel: "Allow analytics cookies",
      saveChoiceLabel: "Save choice",
      closeLabel: "Close",
      currentChoiceLabel: "Current choice",
      necessarySaved: "Necessary cookies only are saved.",
      analyticsSaved: "Analytics cookies are accepted."
    }
  }
};
